<!--
  A view open on part of its load (loading/steps.ts `streaming`): one quiet
  line under the header saying what is still coming in, each with how far
  it is where that is known, in the still-counting treatment (layout.css
  `counting-*`). It goes once everything the view counts with is in. When
  the rest stopped, it says why and offers Try again, the view staying open
  on what it has.

  It takes no focus and announces nothing on its ticks: the region is
  aria-busy while the rest loads, and its Counting status says once that
  it is all in.
-->
<script lang="ts">
  import Counting from '../components/Counting.svelte';
  import { streaming, type LoadView } from './steps';

  interface Props {
    view: LoadView;
    onretry?: () => void;
  }
  let { view, onretry }: Props = $props();

  const s = $derived(streaming(view));
  /** It was up: once it goes, its status says so once. */
  let seen = $state(false);
  $effect(() => {
    if (s) seen = true;
  });
</script>

{#if s}
  <div class="load-strip" class:stopped={!!s.stopped} aria-busy={!s.stopped}>
    {#if s.stopped}
      <span class="load-strip-why" role="alert">Couldn't load the rest of {s.stopped.what}: {s.stopped.failure.message}</span>
      {#if onretry}<button type="button" class="btn small" onclick={onretry}>Try again</button>{/if}
    {:else}
      <span class="load-strip-lead counting-note">Still loading</span>
      {#each s.steps as step (step.id)}
        <span class="load-strip-step" class:active={step.status === 'active'}>
          <span class="load-strip-label">{step.label}</span>
          {#if step.detail}<span class="load-strip-detail counting-figure">{step.detail}</span>{/if}
          {#if step.progress}
            <span class="load-strip-bar counting-bar" role="progressbar" aria-label={step.label} aria-valuemin={0} aria-valuemax={step.progress.total} aria-valuenow={step.progress.done}
              ><i style="width:{Math.round((step.progress.done / step.progress.total) * 100)}%"></i></span
            >
          {/if}
        </span>
      {/each}
    {/if}
  </div>
{/if}
{#if seen}<Counting busy={!!s && !s.stopped} done="Everything on this page is loaded." />{/if}

<style>
  .load-strip {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 14px;
    padding: 6px 12px;
    margin: 0 0 10px;
    border: 1px dashed var(--line);
    border-radius: 8px;
    font-size: 12px;
    color: var(--muted);
  }
  .load-strip.stopped {
    border-style: solid;
    border-color: var(--bad);
    color: var(--fg);
  }
  .load-strip-step {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .load-strip-step.active .load-strip-label {
    color: var(--fg);
  }
  .load-strip-bar {
    display: inline-block;
    width: 56px;
    height: 4px;
    border-radius: 2px;
    background: var(--line);
    overflow: hidden;
  }
  .load-strip-bar i {
    display: block;
    height: 100%;
    background: var(--accent);
  }
</style>
